<template>
  <div ref="contentOrgEl" class="content-organization">
    <!-- plus free access callout -->
    <plus-free-access-callout
      v-if="userAuthStore.plusFreeAccess.enabled"
      tour-id="plusFreeAccessSurpriseMe"
    />

    <search-header
      :title="pageTitle"
      active="filters"
      filter-url="/pilot-programs"
      :show-tags="true"
      :show-toggle="false"
      @tag-update="tagUpdate"
      @clear-all="clearAll"
    />

    <div ref="contentResultsEl" class="content-type-options">
      <!-- No filters on this view -->

      <div class="content-results">
        <!-- error state, modal will also show -->
        <callout v-if="errorMsg" type="error">
          <div>{{ errorMsg }}</div>
        </callout>

        <!-- result top bar -->
        <div class="results-details">
          <search-sort
            :show-filter-toggle="false"
            :total="searchResults.total"
            @sort-update="sortUpdate"
          />
          <search-result-actions
            :categories="isMyPilotPrograms"
            :active-expander="activeExpander"
            :accessibility-expander-id="accessibilityExpanderId"
            @expander-click="toggleActiveExpander"
          />
        </div>

        <!-- expander -->
        <div :id="accessibilityExpanderId" class="results-expander" :aria-hidden="!activeExpander">
          <plan-nav
            v-if="isMyPilotPrograms && activeExpander === 'categories'"
            active-nav-type="pilot"
          />
        </div>

        <!-- results -->
        <search-result-cards
          v-if="!errorMsg && (searchResults.loading || searchResults.total > 0)"
          v-slot="{ cardOrder }"
          :loading="searchResults.loading"
          :per-page-total="searchResults.perPage"
          :current-page-total="searchResultsData.length"
          :entity-type="entityType"
          :show-units="false"
        >
          <!-- custom static cards -->
          <store-card v-if="showStaticCards" />
          <plan-card
            v-for="(plan, index) in searchResultsData"
            :key="plan.id"
            :plan="plan"
            :detail-view="detailView"
            :show-minutes="false"
            :style="{ order: cardOrder(index) }"
          />
        </search-result-cards>
        <search-no-results
          v-else
          :show-sales-cta="
            isMyPilotPrograms && !hasQueryParams && !userAuthStore.features.VIDEO_FILTERS
          "
          sales-page-url="/pilot-programs"
          @clear-all="clearAll"
        />

        <!-- pagination -->
        <pagination
          :current-page="searchResults.currentPage"
          :total-pages="searchResults.totalPages"
          :total-items="searchResults.total"
          :per-page="searchResults.perPage"
          @page-update="pageUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import Pagination from '../search/pagination.vue'
import SearchHeader from '@/components/search/search-header.vue'
import SearchSort from '@/components/search/search-sort.vue'
import SearchResultActions from '@/components/search/search-result-actions.vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import { removeArraySyntax } from '@/utils/helpers'
import { fbApi, isAbortError, objToSearchParams } from '@/utils/http-api'
import type { SortOption, PlanFacets, PlanPagination } from '@/types/search-types'
import Callout from '@/components/notifications/callout.vue'
import SearchResultCards from '@/components/search/search-result-cards.vue'
import PlanCard from '@/components/plans/plan-card.vue'
import SearchNoResults from '@/components/search/search-no-results.vue'
import { useSearchRoute } from '@/composables/search-route'
import PlusFreeAccessCallout from '@/components/notifications/plus-free-access-callout.vue'
import type { PlanCardType } from '@/types/model-types'
import PlanNav from '@/components/plans/plan-nav.vue'
import StoreCard from '@/components/plans/store-card.vue'

/** TYPES **/
type ActiveExpander = 'categories' | ''

/** STATE **/
const route = useRoute()
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()
const { contentResultsEl, contentOrgEl, tagUpdate, sortUpdate, clearAll, pageUpdate } =
  useSearchRoute()

const page = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const searchResults = ref<{
  loading: boolean
  error: string
  currentPage: number
  totalPages: number
  total: number
  perPage: number
}>({
  loading: false,
  error: '',
  currentPage: 0,
  totalPages: 0,
  perPage: 20,
  total: 0,
})

const searchResultsData = shallowRef<PlanCardType[]>([])
const activeExpander = ref<ActiveExpander>('')

let showStaticCards = shallowRef<boolean>(false)

const isMyPilotPrograms = computed(() => route.name === 'MyPilotPrograms')

/** NON REACTIVE **/
let searchInitAbort: AbortController | null = null
let fetchAbort: AbortController | null = null

const accessibilityExpanderId = 'results-expander-control'
const entityType = 'plan'

let sortOptions: SortOption[] = [
  { label: 'Newest First', value: 'newest' },
  { label: 'Title', value: 'title' },
  { label: 'Price', value: 'price' },
]

/** COMPUTED **/
const errorMsg = computed(() => page.value.error || searchResults.value.error)
const pageTitle = computed(() => (isMyPilotPrograms.value ? 'My Pilot Programs' : 'Pilot Programs'))
const detailView = computed(() => (isMyPilotPrograms.value ? 'myPrograms' : 'sales'))
const hasQueryParams = computed(() => Object.keys(route.query).length > 0)

/** WATCHERS **/
watch(
  () => route.query,
  () => {
    // resync on every query string change
    syncUrlToState({ init: false })

    // refresh data
    fetchPlans()
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // clear everything
  searchStore.$reset()

  showStaticCards.value = !isMyPilotPrograms.value

  // remove price sort option as it is not relevant on my programs
  // add title sort
  if (isMyPilotPrograms.value) {
    sortOptions = sortOptions.filter((so) => so.label !== 'Price')
  }

  searchStore.setSortOptions(sortOptions)

  // allow search init to finish first before fetching videos,
  // it will populate team members before syncing state from url
  await searchInit()

  syncUrlToState({ init: true })

  // show static card if no search / filters
  shouldShowStaticCard()

  // do not wait for this to finish before moving on
  fetchPlans()
})

// cancel fetch request, especially if going to browse view as it will
// as it will use the search store
onBeforeUnmount(() => {
  if (searchInitAbort) {
    searchInitAbort.abort()
  }

  if (fetchAbort) {
    fetchAbort.abort()
  }
})

/** METHODS **/

/**
 * Update pinia based on url query params as the source of truth.
 * Clear out values and reset them based on the query params
 */
function syncUrlToState({ init }: { init: boolean }) {
  // removes [] and forces array for items with [] in var names
  const queryParams = removeArraySyntax(route.query)

  // will be passed in the url
  searchStore.setSimilarToId(Number(queryParams.similarto))

  // no checkbox, range, or keyword search options for this view

  searchStore.setSortSelected(queryParams.sort || 'newest')

  // rebuild tags last, after all pinia state is loaded
  searchStore.setInitialTags()
}

/**
 * turn on / off static card when on workout programs page
 * if no search / filters, then show single card
 * if search, disable and show all exclusive programs
 */
function shouldShowStaticCard() {
  if (!isMyPilotPrograms.value) {
    const params = route.query || {}
    const paramKeys = Object.keys(params)
    showStaticCards.value = !paramKeys || !paramKeys.length
  }
}

async function fetchPlans() {
  // clear any error
  searchResults.value.error = ''
  searchResults.value.loading = true

  // format GET query params
  let params = route.query || {}
  params = removeArraySyntax(params)

  // force pilot programs only (taxonomyId)
  params.plantype = ['97']

  // reset pagination on a filter change
  if (!('page' in params)) {
    searchResults.value.currentPage = 1
  }

  try {
    // cancel any previous fetch
    if (fetchAbort) {
      fetchAbort.abort()
    }

    // re-init abort controller
    fetchAbort = new AbortController()

    const apiUrl = isMyPilotPrograms.value ? `/plans/purchased` : `/plans`

    const res = await fbApi.get(apiUrl, {
      searchParams: objToSearchParams(params),
      signal: fetchAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch workouts, please refresh the page and try again`)
    }

    const {
      paginated_plans,
      facets,
    }: {
      paginated_plans: PlanPagination
      facets: PlanFacets
    } = res.body.data

    searchResults.value.total = paginated_plans.total
    searchResults.value.currentPage = paginated_plans.current_page
    searchResults.value.totalPages = paginated_plans.last_page
    searchResultsData.value = paginated_plans.data

    // pinia facets
    searchStore.updateFacets(facets)

    // show static card if no search / filters
    shouldShowStaticCard()
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      console.warn('fetchPlans aborted')
      return
    }

    // oops model will show
    console.error(err)
    searchResults.value.error = `Unable to fetch pilot programs, please refresh the page and try again`
  } finally {
    searchResults.value.loading = false
  }
}

/**
 * Load state into searchStore
 */
async function searchInit() {
  // clear any error and set both loading stats so skeleton cards show
  page.value.error = ''
  page.value.loading = true
  searchResults.value.loading = true

  try {
    searchInitAbort = new AbortController()

    const res = await fbApi.get('/plans/search-init', {
      signal: searchInitAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch programs, please refresh the page and try again`)
    }

    const {
      storeCardSaleAll,
    }: {
      storeCardSaleAll: boolean
    } = res.body.data

    searchStore.setSearchInit({
      storeCardSaleAll,
    })
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      return
    }

    // oops model will show
    console.error(err)
    page.value.error = `Unable to initialize search, please refresh the page and try again`
  } finally {
    page.value.loading = false
  }
}

function toggleActiveExpander(expander: ActiveExpander) {
  if (activeExpander.value === expander) {
    activeExpander.value = ''
    return
  }

  activeExpander.value = expander
}
</script>
